import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Loader2, MessageSquareWarning, X, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { SEVERITY_STYLES, type ModerationSeverityLevel } from "@/lib/moderation";
import { useModerationRuleLabelMap } from "@/hooks/use-moderation-rule-labels";
import { humanizeKey } from "@/components/product-detail/product-detail-utils";
import { ModerationRulesetService } from "@/services/moderation-ruleset.service";
import {
  ReviewService,
  type ApproveResponse,
  type ModerationFeedbackItem,
  type ModerationStageResult,
  type ModerationResult,
  type ModerationViolation,
  type RejectResponse,
  type ReviewQueueCounts,
} from "@/services/review.service";

interface ReviewQueueProps {
  catalogueId: string;
  deletedAt?: string;
}

type Tab = "queue" | "approved" | "rejected" | "feedback";
type ReviewStatus = "pending" | "approved" | "rejected";
type SeverityFilter = "" | "low" | "medium" | "high" | "critical";

const PAGE_SIZE = 50;

const REVIEW_STATUS_BY_TAB: Record<Exclude<Tab, "feedback">, ReviewStatus> = {
  queue: "pending",
  approved: "approved",
  rejected: "rejected",
};

function severityBadgeClass(severity: string): string {
  return (
    SEVERITY_STYLES[severity?.toLowerCase() as ModerationSeverityLevel]?.badgeClass ??
    "border-muted bg-muted/50 text-muted-foreground"
  );
}

function formatTimestamp(value: Date | null | undefined) {
  return value ? value.toISOString().slice(0, 16).replace("T", " ") : "";
}

function ViolationChips({
  violations,
  labelFor,
}: {
  violations: ModerationViolation[];
  labelFor: (slug: string) => string | undefined;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {violations.map((violation) => (
        <span
          key={`${violation.slug}-${violation.imageIndex ?? "x"}`}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
            severityBadgeClass(violation.severity),
          )}
        >
          {labelFor(violation.slug) ?? humanizeKey(violation.slug)}
          {violation.unverified && <span title="Verification pass failed; kept fail-safe">· unverified</span>}
        </span>
      ))}
    </div>
  );
}

function stageTitle(stage: ModerationStageResult["dataStage"]): string {
  if (stage === "original") return "Original product data";
  if (stage === "ai_enhanced") return "AI-enhanced product data";
  return humanizeKey(stage);
}

function StageResultSection({
  stage,
  catalogueId,
  readOnly,
  labelFor,
  onApproved,
  onRejected,
}: {
  stage: ModerationStageResult;
  catalogueId: string;
  readOnly: boolean;
  labelFor: (slug: string) => string | undefined;
  onApproved: (response: ApproveResponse) => void;
  onRejected: (response: RejectResponse) => void;
}) {
  const [feedbackBySlug, setFeedbackBySlug] = useState<Record<string, string>>({});
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(
    () => new Set(stage.violations.map((violation) => violation.slug)),
  );
  const [retainedImagesBySlug, setRetainedImagesBySlug] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decided = stage.reviewStatus === "approved" || stage.reviewStatus === "rejected";
  const actionable = stage.status === "flagged";

  const approve = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const feedback = Array.from(selectedSlugs)
        .map((slug) => ({
          slug,
          text: feedbackBySlug[slug]?.trim() ?? "",
          retained_image_urls: retainedImagesBySlug[slug] ?? [],
        }))
        .filter((item) => item.text || item.retained_image_urls.length > 0);
      const response = await ReviewService.approve(catalogueId, stage.productId, {
        data_stage: stage.dataStage,
        rule_slugs: Array.from(selectedSlugs),
        feedback: feedback.length ? feedback : undefined,
      });
      onApproved(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }, [catalogueId, feedbackBySlug, onApproved, retainedImagesBySlug, selectedSlugs, stage.dataStage, stage.productId]);

  const toggleRetainedImage = useCallback((slug: string, url: string, checked: boolean) => {
    setRetainedImagesBySlug((previous) => {
      const current = new Set(previous[slug] ?? []);
      if (checked) current.add(url);
      else current.delete(url);
      return { ...previous, [slug]: Array.from(current) };
    });
  }, []);

  const reject = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await ReviewService.reject(catalogueId, stage.productId, { data_stage: stage.dataStage });
      onRejected(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }, [catalogueId, onRejected, stage.dataStage, stage.productId]);

  return (
    <section className="rounded-xl border p-4" aria-labelledby={`stage-${stage.dataStage}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id={`stage-${stage.dataStage}`} className="text-base font-semibold text-foreground">
            {stageTitle(stage.dataStage)}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {humanizeKey(stage.status)}
            {stage.unpublishApplied && <span className="ml-1 font-semibold text-red-600">· offers unpublished</span>}
          </p>
        </div>
        <Badge variant="outline">Review: {humanizeKey(stage.reviewStatus)}</Badge>
      </div>

      {stage.imageUrls.length > 0 && (
        <div className="mt-4 flex gap-2 overflow-x-auto">
          {stage.imageUrls.map((url, index) => (
              <img
                key={url}
                src={url}
                alt={`Product image ${index + 1}`}
                className="h-28 w-28 flex-shrink-0 rounded-lg border object-cover"
              />
          ))}
        </div>
      )}

      {(stage.name || stage.description) && (
        <div className="mt-3 rounded-lg bg-muted/40 p-3">
          {stage.name && <p className="text-sm font-medium text-foreground">{stage.name}</p>}
          {stage.description && <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{stage.description}</p>}
        </div>
      )}

      {stage.passedSlugs.length > 0 && (
        <div className="mt-4">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Passed rules</span>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {stage.passedSlugs.map((slug) => (
              <Badge key={slug} variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                {labelFor(slug) ?? humanizeKey(slug)}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Violations</span>
        {stage.violations.length === 0 && (
          <p className="text-sm text-muted-foreground">No violations for this stage.</p>
        )}
        {stage.violations.map((violation) => (
            <div key={`${violation.slug}-${violation.imageIndex ?? "x"}`} className="rounded-xl border p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {!readOnly && actionable && !decided && (
                    <input
                      type="checkbox"
                      checked={selectedSlugs.has(violation.slug)}
                      onChange={(event) => {
                        setSelectedSlugs((previous) => {
                          const next = new Set(previous);
                          if (event.target.checked) next.add(violation.slug);
                          else next.delete(violation.slug);
                          return next;
                        });
                      }}
                      aria-label={`Select ${labelFor(violation.slug) ?? humanizeKey(violation.slug)} result`}
                    />
                  )}
                  <span className={cn("rounded-full border px-2 py-0.5 text-[11px] font-semibold", severityBadgeClass(violation.severity))}>
                    {violation.severity}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {labelFor(violation.slug) ?? humanizeKey(violation.slug)}
                  </span>
                  <span className="text-[10px] text-muted-foreground">v{violation.ruleVersion}</span>
                </div>
                {violation.imageIndex != null && (
                  <span className="text-[10px] text-muted-foreground">image #{violation.imageIndex + 1}</span>
                )}
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">{violation.evidence}</p>
              {!readOnly && actionable && !decided && selectedSlugs.has(violation.slug) && (
                <div className="mt-2 space-y-2">
                  <Textarea
                    placeholder="Optional feedback explaining why this result is a false positive…"
                    value={feedbackBySlug[violation.slug] ?? ""}
                    onChange={(e) => setFeedbackBySlug((prev) => ({ ...prev, [violation.slug]: e.target.value }))}
                    className="min-h-16"
                  />
                  {stage.imageUrls.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground">Explicitly retain selected images as regression evidence (none retained by default)</p>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {stage.imageUrls.map((url, index) => (
                          <label key={`${violation.slug}-${url}`} className="relative cursor-pointer">
                            <img src={url} alt={`Retain product image ${index + 1}`} className="h-14 w-14 rounded-md border object-cover" />
                            <input
                              type="checkbox"
                              checked={(retainedImagesBySlug[violation.slug] ?? []).includes(url)}
                              onChange={(event) => toggleRetainedImage(violation.slug, url, event.target.checked)}
                              className="absolute right-1 top-1"
                              aria-label={`Retain image ${index + 1} for ${labelFor(violation.slug) ?? humanizeKey(violation.slug)}`}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
        ))}
      </div>

      {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">{error}</div>}

      <div className="mt-5 flex items-center justify-end gap-2">
          {stage.reviewStatus === "approved" && (
            <span className="flex items-center gap-1.5 text-sm font-semibold text-green-600">
              <CheckCircle2 size={15} /> Approved{stage.reviewedBy ? ` by ${stage.reviewedBy}` : ""}
            </span>
          )}
          {stage.reviewStatus === "rejected" && (
            <span className="flex items-center gap-1.5 text-sm font-semibold text-red-600">
              <XCircle size={15} /> Rejected{stage.reviewedBy ? ` by ${stage.reviewedBy}` : ""}
            </span>
          )}
          {!readOnly && actionable && !decided && (
            <Button
              variant="outline"
              disabled={submitting}
              onClick={reject}
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              title="Keeps the product's offers unpublished"
            >
              {submitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Reject
            </Button>
          )}
          {!readOnly && actionable && stage.reviewStatus !== "approved" && (
            <Button
              disabled={submitting || selectedSlugs.size === 0}
              onClick={approve}
              className="bg-green-600 hover:bg-green-500"
              title="Approves the product and republishes any offers moderation unpublished"
            >
              {submitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Approve selected ({selectedSlugs.size})
            </Button>
          )}
      </div>
    </section>
  );
}

function ResultDetail({
  result,
  catalogueId,
  readOnly,
  labelFor,
  onClose,
  onApproved,
  onRejected,
}: {
  result: ModerationResult;
  catalogueId: string;
  readOnly: boolean;
  labelFor: (slug: string) => string | undefined;
  onClose: () => void;
  onApproved: (response: ApproveResponse) => void;
  onRejected: (response: RejectResponse) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-card p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">{result.name || result.productId}</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {humanizeKey(result.status)} · {result.stageResults.length} data {result.stageResults.length === 1 ? "stage" : "stages"}
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted" aria-label="Close review details">
            <X size={16} />
          </button>
        </div>
        <div className="mt-5 space-y-4">
          {result.stageResults.map((stage) => (
            <StageResultSection
              key={stage.dataStage}
              stage={stage}
              catalogueId={catalogueId}
              readOnly={readOnly}
              labelFor={labelFor}
              onApproved={onApproved}
              onRejected={onRejected}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function ReviewQueue({ catalogueId, deletedAt }: ReviewQueueProps) {
  const readOnly = Boolean(deletedAt);
  const [tab, setTab] = useState<Tab>("queue");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<ModerationResult[]>([]);
  const [total, setTotal] = useState(0);
  const [feedback, setFeedback] = useState<ModerationFeedbackItem[]>([]);
  const [counts, setCounts] = useState<ReviewQueueCounts>({ pending: 0, approved: 0, rejected: 0 });
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("");
  const [slugFilter, setSlugFilter] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [ruleSlugs, setRuleSlugs] = useState<string[] | null>(null);
  const [filterRulesetId, setFilterRulesetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Counts + pending feedback always load so every tab label stays fresh;
      // the feedback list doubles as the Feedback tab's data.
      const [queueCounts, feedbackData, tabData] = await Promise.all([
        ReviewService.queueCounts(catalogueId),
        ReviewService.listFeedback(catalogueId, "all"),
        tab === "feedback"
          ? Promise.resolve(null)
          : ReviewService.queue(catalogueId, {
              review_status: REVIEW_STATUS_BY_TAB[tab],
              severity: severityFilter || undefined,
              slug: slugFilter || undefined,
              order,
              limit: PAGE_SIZE,
              offset,
            }),
      ]);
      setCounts(queueCounts);
      setFeedback(feedbackData.items);
      if (tabData) {
        setItems(tabData.items);
        setTotal(tabData.total);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [catalogueId, offset, order, severityFilter, slugFilter, tab]);

  useEffect(() => { load(); }, [load]);

  // Rule slugs for the filter dropdown come from this catalogue's assigned
  // global ruleset. Inactive rules are omitted because they cannot create new
  // violations.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const assignment = await ModerationRulesetService.getCatalogueAssignment(catalogueId);
        if (!assignment) {
          if (!cancelled) {
            setRuleSlugs([]);
            setFilterRulesetId(null);
          }
          return;
        }
        const rules = await ModerationRulesetService.listRules(assignment.rulesetId);
        if (!cancelled) {
          setRuleSlugs(
            rules
              .filter((rule) => rule.active)
              .map((rule) => rule.slug)
              .sort(),
          );
          setFilterRulesetId(assignment.rulesetId);
        }
      } catch {
        if (!cancelled) setRuleSlugs(null);
      }
    })();
    return () => { cancelled = true; };
  }, [catalogueId]);

  const selected = useMemo(
    () => items.find((item) => item.productId === selectedProductId) ?? null,
    [items, selectedProductId],
  );

  // Human-readable rule names for the slugs shown across the queue; covers the
  // rulesets of the loaded results plus the one backing the rule filter.
  const queueRulesetIds = useMemo(
    () => [...new Set(
      items.flatMap((item) => item.stageResults.map((stage) => stage.rulesetId)).filter(Boolean),
    )],
    [items],
  );
  const { labelFor } = useModerationRuleLabelMap([...queueRulesetIds, filterRulesetId]);

  const onApproved = useCallback(async (response: ApproveResponse) => {
    setNotice(
      response.republished ? "Offers republished." : (
        response.result.reviewStatus === "pending"
          ? "Selected rule result approved; other rule holds remain."
          : "Selected rule results approved."
      ),
    );
    setSelectedProductId(null);
    await load();
  }, [load]);

  const onRejected = useCallback(async (response: RejectResponse) => {
    setNotice(response.unpublished ? "Product rejected — offers unpublished." : "Product rejected.");
    setSelectedProductId(null);
    await load();
  }, [load]);

  const decideFeedback = useCallback(async (item: ModerationFeedbackItem, action: "accept" | "reject") => {
    setBusy(true);
    setError(null);
    try {
      if (action === "accept") {
        const response = await ReviewService.acceptFeedback(catalogueId, item.id);
        setNotice(
          `Rule "${labelFor(item.ruleSlug) ?? humanizeKey(item.ruleSlug)}" version ${response.newRuleVersion} is preparing. The current ready version keeps running until the update is ready.`,
        );
      } else {
        await ReviewService.rejectFeedback(catalogueId, item.id);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [catalogueId, load, labelFor]);

  const tabs: { key: Tab; label: string }[] = [
    { key: "queue", label: `Review queue (${counts.pending})` },
    { key: "approved", label: `Approved (${counts.approved})` },
    { key: "rejected", label: `Rejected (${counts.rejected})` },
    { key: "feedback", label: `Feedback (${feedback.filter((item) => item.status === "pending").length})` },
  ];

  const emptyLabel =
    tab === "queue" ? "No products waiting for review 🎉"
    : tab === "approved" ? "No approved products yet"
    : "No rejected products";

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Flagged products wait here; publish to approve them or reject to keep their offers unpublished.
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setTab(key); setOffset(0); setNotice(null); }}
              className={cn(
                "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
                tab === key
                  ? "border-primary bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab !== "feedback" && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3 shadow-sm">
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Severity</span>
            <Select
              value={severityFilter || "all"}
              onValueChange={(value) => {
                setSeverityFilter(value === "all" ? "" : (value as SeverityFilter));
                setOffset(0);
              }}
            >
              <SelectTrigger className="h-9 w-[140px]" aria-label="Filter by severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {ruleSlugs && ruleSlugs.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Rule</span>
              <Select
                value={slugFilter || "all"}
                onValueChange={(value) => { setSlugFilter(value === "all" ? "" : value); setOffset(0); }}
              >
                <SelectTrigger className="h-9 w-[220px]" aria-label="Filter by rule">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All rules</SelectItem>
                  {ruleSlugs.map((slug) => (
                    <SelectItem key={slug} value={slug}>{labelFor(slug) ?? humanizeKey(slug)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Sort</span>
            <Select
              value={order}
              onValueChange={(value) => { setOrder(value as "asc" | "desc"); setOffset(0); }}
            >
              <SelectTrigger className="h-9 w-[150px]" aria-label="Sort order">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest first</SelectItem>
                <SelectItem value="asc">Oldest first</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(severityFilter || slugFilter) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-2 text-xs"
              onClick={() => { setSeverityFilter(""); setSlugFilter(""); setOffset(0); }}
            >
              Clear filters
            </Button>
          )}
        </div>
      )}

      {notice && <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-900 dark:bg-green-950/40 dark:text-green-300">{notice}</div>}
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : tab === "feedback" ? (
        <div className="space-y-3">
          {feedback.length === 0 && (
            <div className="py-16 text-center text-sm text-muted-foreground">No review feedback yet</div>
          )}
          {feedback.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <MessageSquareWarning size={15} className="text-amber-500" />
                    <span className="text-sm font-semibold text-foreground">
                      {labelFor(item.ruleSlug) ?? humanizeKey(item.ruleSlug)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">rule v{item.ruleVersion}</span>
                    <Badge
                      variant="outline"
                      className={cn(
                        item.status === "accepted"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
                          : item.status === "rejected"
                            ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
                            : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
                      )}
                    >
                      {item.status}
                    </Badge>
                  </div>
                  {!readOnly && item.status === "pending" && (
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busy} onClick={() => decideFeedback(item, "accept")}>
                        Accept feedback
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => decideFeedback(item, "reject")}>
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
                <p className="mt-2 rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground">{item.feedbackText}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          {severityFilter || slugFilter ? "No products match the current filters" : emptyLabel}
        </div>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[64px]" />
                    <TableHead>Product</TableHead>
                    <TableHead>Violations</TableHead>
                    <TableHead className="w-[120px] text-center">Status</TableHead>
                    <TableHead className="w-[150px] text-right">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.productId}
                      className="cursor-pointer"
                      onClick={() => setSelectedProductId(item.productId)}
                    >
                      <TableCell>
                        <div className="h-11 w-11 overflow-hidden rounded-lg border bg-muted">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt="" className="h-full w-full object-cover" />
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[240px]">
                        <span className="block truncate font-medium text-foreground">
                          {item.name || item.productId}
                        </span>
                      </TableCell>
                      <TableCell>
                        <ViolationChips violations={item.violations} labelFor={labelFor} />
                      </TableCell>
                      <TableCell className="text-center">
                        {item.unpublishApplied ? (
                          <Badge
                            variant="outline"
                            className="border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
                          >
                            Unpublished
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {formatTimestamp(item.updatedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 pt-1 text-sm text-muted-foreground">
              <button
                disabled={offset === 0}
                onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
                className="rounded-lg border px-3 py-1 disabled:opacity-30 hover:bg-muted"
              >Prev</button>
              <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
              <button
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset((value) => value + PAGE_SIZE)}
                className="rounded-lg border px-3 py-1 disabled:opacity-30 hover:bg-muted"
              >Next</button>
            </div>
          )}
        </>
      )}

      {selected && (
        <ResultDetail
          result={selected}
          catalogueId={catalogueId}
          readOnly={readOnly}
          labelFor={labelFor}
          onClose={() => setSelectedProductId(null)}
          onApproved={onApproved}
          onRejected={onRejected}
        />
      )}
    </div>
  );
}
